<script setup>
import { computed, ref, provide, watch } from 'vue'
import { useBillingStore } from '@/stores/BillingStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useUserStore } from '@/stores/UserStore'
import { useRoute } from 'vue-router'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import PlanSection from './components/PlanSection.vue'
import { getPlanForFeature } from '@/helpers/plans'
import { formatDate } from '@/helpers/times'
import UpgradeYellowBlock from '@/components/UpgradePlans/UpgradeYellowBlock.vue'
import { UPGRADE_MODALS, QUICK_UPGRADE_MODAL_TYPES, STRIPE_ERRORS } from '@/constants'
import { POSTHOG_EVENTS } from '@/constants/posthog'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { handleError } from '@/services/errorHandler'
import { safeCapture } from '@/helpers/posthog'

const billingStore = useBillingStore()
const configStore = useConfigStore()
const userStore = useUserStore()
const route = useRoute()

const stripeError = ref(null)
const haveSavedPayment = computed(() => !!userStore.user?.stripe_default_payment_method)
const paymentLoader = ref(false)
const planUpdated = ref(false)
const pendingUpgrade = ref(false)

provide('stripeError', stripeError)

const upgradeFeatureModalData = computed(() => billingStore.upgradeFeatureModalData)
const currentPlan = computed(() => configStore.config.hasActiveSubscription ? billingStore.currentPlan : {})
const paymentError = computed(() => billingStore.paymentError)
const updateSubscriptionSuccess = computed(() => billingStore.updateSubscriptionSuccess)
const updateSubscriptionError = computed(() => billingStore.updateSubscriptionError)

const targetPlan = computed(() => {
  if (upgradeFeatureModalData.value?.plan) {
    return {
      ...UPGRADE_MODALS[QUICK_UPGRADE_MODAL_TYPES.UPGRADE_PLAN],
      ...upgradeFeatureModalData.value?.plan,
    }
  }

  const type = upgradeFeatureModalData.value?.type
  
  return type ? getPlanForFeature(type) : billingStore.plansOptions?.[billingStore.plansOptions.length - 1] || {}
})

const isAnnual = computed(() => billingStore.plansPeriods.selected === 'annual')
// Upgrade window: respect the period toggle. Feature gate: use current subscription cycle (default monthly).
const useAnnualPricing = computed(() => {
  return upgradeFeatureModalData.value?.isPlanUpgradeModal
    ? isAnnual.value
    : (configStore.config.subscription?.annual ?? false)
})

const isActiveSubscription = computed(() => configStore.getConfigField('hasActiveSubscription'))

const planCost = computed(() =>
  useAnnualPricing.value
    ? (targetPlan.value?.price?.annual_final || 0)
    : (targetPlan.value?.price?.monthly || 0)
)

const proratedCharge = computed(() => {
  if (!isActiveSubscription.value) return planCost.value

  const currentCost = useAnnualPricing.value
    ? (billingStore.currentPlan?.price?.annual_final || 0)
    : (billingStore.currentPlan?.price?.monthly || 0)

  const diff = Math.max(0, planCost.value - currentCost)
  if (diff === 0) return 0

  const expiration = configStore.config?.subscription?.expiration_date
  if (!expiration) return diff

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expDate = new Date(expiration)
  expDate.setHours(0, 0, 0, 0)

  const daysRemaining = Math.max(1, Math.round((expDate - today) / (1000 * 60 * 60 * 24)) + 1)
  const daysInPeriod = useAnnualPricing.value
    ? 365
    : new Date(expDate.getFullYear(), expDate.getMonth() + 1, 0).getDate()

  return (daysRemaining / daysInPeriod) * diff
})

const nextBillingDate = computed(() => {
  const expiration = configStore.config?.subscription?.expiration_date
  if (!expiration) return formatDate(new Date())
  const next = new Date(expiration)
  next.setDate(next.getDate() + 1)
  return formatDate(next)
})

watch(() => billingStore.upgradeFeatureModal, (val) => {
  if (!val) return

  safeCapture(POSTHOG_EVENTS.PAYWALL_VIEWED, {
    paywall_type: upgradeFeatureModalData.value?.paywallType || (upgradeFeatureModalData.value?.isPlanUpgradeModal ? 'upgrade_modal' : 'feature_gate'),
    current_plan: currentPlan.value?.name || currentPlan.value?.label,
    gated_feature: upgradeFeatureModalData.value?.type || 'unknown',
    suggested_plan: targetPlan.value?.name || targetPlan.value?.label,
    source_page: route.name,
    entitlement_key: null,
  })
})

const trackPlanChangeInitiated = () => {
  const changeType = currentPlan.value?.id < targetPlan.value?.id ? 'upgrade' : 'downgrade'
  const eventName = changeType === 'upgrade'
    ? POSTHOG_EVENTS.SUBSCRIPTION_UPGRADE_INITIATED
    : POSTHOG_EVENTS.SUBSCRIPTION_DOWNGRADE_INITIATED

  safeCapture(eventName, {
    current_plan: currentPlan.value?.name || currentPlan.value?.label,
    target_plan: targetPlan.value?.name || targetPlan.value?.label,
    change_type: changeType,
    trigger: upgradeFeatureModalData.value?.type || 'unknown',
    source_page: route.name,
  })
}

const closeDialog = () => {
  // if (billingStore.upgradeWindow) return
  billingStore.upgradeFeatureModal = false
}

const upgradeNow = async () => {
  stripeError.value = ''
  paymentLoader.value = true
  pendingUpgrade.value = true

  trackPlanChangeInitiated()

  try {
    await billingStore.updateSubscription({ plan_id: targetPlan.value.id, taxRate: userStore.tax?.taxRate, annual: useAnnualPricing.value })
    userStore.fetchUser()
    billingStore.fetchInvoices()

    billingStore.upgradeFeatureModal = false

  } catch (error) {
    paymentLoader.value = false
    stripeError.value = 'An error occurred. Please try again.'
    handleError(error, { category: 'component', action: 'upgrade-now', component: 'UpgradeFeatureModal' })
  }
}

const subscribeNow = async () => {
  stripeError.value = ''
  paymentLoader.value = true
  pendingUpgrade.value = true

  trackPlanChangeInitiated()

  try {
    await billingStore.activeSubscription({ 
      plan_id: targetPlan.value.id,
      stripe_payment_method_id: userStore.user.stripe_default_payment_method?.id,
      taxRate: userStore.tax?.taxRate,
      annual: useAnnualPricing.value
    })
  } catch (error) {
    paymentLoader.value = false
    stripeError.value = 'An error occurred. Please try again.'
    handleError(error, { category: 'component', action: 'subscribe-now', component: 'UpgradeFeatureModal' })
  }
}

watch(paymentError, async () => {
  if (!pendingUpgrade.value) return

  if (paymentError.value.message && !paymentError.value.ok) {
    paymentLoader.value = false
    pendingUpgrade.value = false
    stripeError.value = STRIPE_ERRORS[paymentError.value.error?.code] || STRIPE_ERRORS[paymentError.value.error?.decline_code] || paymentError.value.message
  } else if (paymentError.value.data && !paymentError.value.data?.ok) {
    paymentLoader.value = false
    pendingUpgrade.value = false
    stripeError.value = STRIPE_ERRORS[paymentError.value.data?.error?.code] || STRIPE_ERRORS[paymentError.value.data?.error?.decline_code] || paymentError.value.data?.error?.message
  } else if (paymentError.value.ok) {
    paymentLoader.value = false
    pendingUpgrade.value = false
    planUpdated.value = true

    configStore.fetchConfigs()
    billingStore.fetchInvoices()
    userStore.fetchUser()

    billingStore.upgradeFeatureModal = false
    billingStore.upgradePlanSuccessModal = true
  }
})

watch([updateSubscriptionSuccess, updateSubscriptionError], () => {
  if (!pendingUpgrade.value) return

  if (updateSubscriptionSuccess.value || updateSubscriptionError.value) {
    paymentLoader.value = false
    pendingUpgrade.value = false
  }

  if (updateSubscriptionSuccess.value) {
    planUpdated.value = true
    billingStore.upgradeFeatureModal = false
    billingStore.upgradePlanSuccessModal = true
  }

  if (updateSubscriptionError.value) {
    const err = billingStore.paymentError
    stripeError.value = STRIPE_ERRORS[err?.error?.code] || STRIPE_ERRORS[err?.error?.decline_code] || err?.message || 'An error occurred while updating your plan. Please try again.'
  }
})
</script>

<template>
  <section>
    <TransitionRoot as="template" :show="billingStore.upgradeFeatureModal && (!billingStore.upgradeWindow || !!billingStore.upgradeFeatureModalData?.isPlanUpgradeModal)">
      <Dialog as="div" class="relative z-[999999]" :class="{'z-[999]': billingStore.upgradeWindow && !billingStore.upgradeFeatureModalData?.isPlanUpgradeModal}" @close="closeDialog">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black bg-opacity-[0.38] transition-opacity backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 z-10">
          <div class="flex h-full w-full justify-center md:items-center md:p-[30px]">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
              enter-to="opacity-100 translate-y-0 md:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 md:scale-100"
              leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            >
              <DialogPanel class="
                relative md:rounded-xl bg-white shadow-lg transition-all w-full md:max-w-[480px] md:max-h-[700px] flex-1
                flex flex-col overflow-hidden
              ">
                <button @click="closeDialog" class="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-7 h-7 rounded-full bg-gray-50 p-[5px]">
                  <Icon icon="heroicons:x-mark-16-solid" class="text-lg text-gray-500" />
                </button>

                <div class="flex flex-col gap-y-4 p-4 md:p-6 flex-1 overflow-y-auto">
                  <div class="flex items-start gap-x-2">
                    <img v-if="upgradeFeatureModalData?.image" :src="upgradeFeatureModalData.image" alt="Upgrade" class="object-contain w-30 h-20" />
                    <UpgradeYellowBlock
                      class="max-md:mt-12"
                      v-if="upgradeFeatureModalData?.isPlanUpgradeModal"
                      :text="upgradeFeatureModalData?.isPlanUpgradeModal ? 'Let\'s go!' : 'Downgrade'"
                    />
                  </div>
                  <div class="flex flex-col gap-y-2">
                    <h3 class="text-xl font-bold text-gray-900">{{ upgradeFeatureModalData.title(targetPlan.label, upgradeFeatureModalData?.isUpgrade ? 'Upgrade' : 'Downgrade') }}</h3>
                    <p class="text-sm text-gray-500 font-medium">
                      {{ upgradeFeatureModalData.description(targetPlan) }}
                    </p>
                  </div>

                  <PlanSection :targetPlan="targetPlan" />

                  <div v-if="stripeError" class="flex items-center justify-center w-full">
                    <span class="text-xs text-red-600 font-normal w-full text-center">{{ stripeError }}</span>
                  </div>

                  <button
                    v-if="isActiveSubscription"
                    class="primary-button w-full py-2.5"
                    :disabled="!haveSavedPayment || paymentLoader"
                    @click="upgradeNow"
                  >
                    <LoadingSpinner v-if="paymentLoader" :width="'19'" :height="'19'" :background="'transparent'" />
                    <span v-else>{{ billingStore.currentPlan.id < targetPlan.id ? 'Upgrade Now' : 'Downgrade Now' }}</span>
                  </button>

                  <button
                    v-else
                    class="primary-button w-full py-2.5"
                    :disabled="!haveSavedPayment || paymentLoader"
                    @click="subscribeNow"
                  >
                    <LoadingSpinner v-if="paymentLoader" :width="'19'" :height="'19'" :background="'transparent'" />
                    <span v-else>Subscribe Now</span>
                  </button>

                  <span class="text-xs text-gray-400 font-normal w-full text-center">
                    <template v-if="isActiveSubscription">
                      You will be immediately charged a pro-rated amount of ${{ proratedCharge.toFixed(2) }}, with subsequent charges of ${{ planCost.toFixed(2) }}/{{ useAnnualPricing ? 'year' : 'mo' }} starting from {{ nextBillingDate }}.
                    </template>
                    <template v-else>
                      You will be charged ${{ planCost.toFixed(2) }}/{{ useAnnualPricing ? 'year' : 'mo' }} starting from {{ formatDate(new Date()) }}.
                    </template>
                  </span>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>  
  </section>
 
</template>